import type { AsyncSequence } from "#public"

import { Join } from "./Join"

/**
 * Maps over async sequences, transforming each item with a function.
 * Items are processed sequentially, one at a time.
 *
 * @param func - Transformation function to apply to each item
 * @param arrays - One or more async sequences to map over
 * @returns An async generator yielding transformed values
 *
 * @example
 * ```ts
 * import { MapAsync } from "@pdfking/tfunc"
 *
 * async function* numbers() {
 *   yield 1;
 *   yield 2;
 *   yield 3;
 * }
 *
 * const doubled = MapAsync(n => n * 2, numbers());
 * for await (const value of doubled) {
 *   console.log(value); // 2, 4, 6
 * }
 * ```
 */
export const MapAsync = async function* <T, N>(
  func: (item: T, index: number) => N | Promise<N>,
  ...arrays: AsyncSequence<T>[]
): AsyncGenerator<N> {
  let index = 0
  for await (const item of Join(...arrays)) {
    yield await func(item, index++)
  }
}
